/**
 * ScreenshotCapture - 영역 선택 캡처 (캡처된 정적 이미지 기반)
 *
 * Capture First, Report Later 원칙:
 * 1. 이미 캡처된 전체 이미지를 수신
 * 2. 전체 화면에 이미지를 표시 + 반투명 어둠 오버레이
 * 3. 마우스/터치 드래그로 영역 선택 (선택 영역만 밝게)
 * 4. 선택 확인 → Canvas drawImage()로 해당 영역만 크롭
 * 5. 크롭된 Blob을 콜백으로 전달
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Check, X, RotateCcw, Maximize } from 'lucide-react';

interface Props {
  fullScreenBlob: Blob;
  onCapture: (croppedBlob: Blob) => void;
  onCancel: () => void;
}

interface SelectionRect {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

export const ScreenshotCapture: React.FC<Props> = ({ fullScreenBlob, onCapture, onCancel }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [selection, setSelection] = useState<SelectionRect | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // ── 이미지 로드 ──
  useEffect(() => {
    const url = URL.createObjectURL(fullScreenBlob);
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
      drawCanvas(img, null);
    };
    img.src = url;

    return () => URL.revokeObjectURL(url);
  }, [fullScreenBlob]);

  // ── 캔버스에 이미지 + 어둠 오버레이 + 선택 영역 그리기 ──
  const drawCanvas = useCallback((img: HTMLImageElement, sel: SelectionRect | null) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext('2d')!;

    // 이미지를 전체 화면에 맞춰 그리기
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // 반투명 어둠 오버레이
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // 선택 영역만 밝게 (원본 이미지 다시 그리기)
    if (sel) {
      const x = Math.min(sel.startX, sel.endX);
      const y = Math.min(sel.startY, sel.endY);
      const w = Math.abs(sel.endX - sel.startX);
      const h = Math.abs(sel.endY - sel.startY);

      if (w > 2 && h > 2) {
        ctx.save();
        ctx.beginPath();
        ctx.rect(x, y, w, h);
        ctx.clip();
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.restore();

        // 선택 영역 테두리
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.8)';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(x, y, w, h);
        ctx.setLineDash([]);

        // 크기 표시
        ctx.fillStyle = 'rgba(59, 130, 246, 0.9)';
        ctx.font = '12px sans-serif';
        const label = `${Math.round(w)} × ${Math.round(h)}`;
        const labelWidth = ctx.measureText(label).width + 12;
        ctx.fillRect(x, y - 22, labelWidth, 20);
        ctx.fillStyle = '#fff';
        ctx.fillText(label, x + 6, y - 7);
      }
    }
  }, []);

  // ── selection 변경 시 다시 그리기 ──
  useEffect(() => {
    if (imageRef.current && imageLoaded) {
      drawCanvas(imageRef.current, selection);
    }
  }, [selection, imageLoaded, drawCanvas]);

  // ── 마우스/터치 핸들러 ──
  const handlePointerDown = (e: React.PointerEvent) => {
    setIsSelecting(true);
    setSelection({ startX: e.clientX, startY: e.clientY, endX: e.clientX, endY: e.clientY });
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isSelecting || !selection) return;
    setSelection(prev => prev ? { ...prev, endX: e.clientX, endY: e.clientY } : null);
  };

  const handlePointerUp = () => {
    setIsSelecting(false);
  };

  // ── 선택 영역 크롭 ──
  const handleConfirm = useCallback(async () => {
    if (!selection || !imageRef.current) return;

    const x = Math.min(selection.startX, selection.endX);
    const y = Math.min(selection.startY, selection.endY);
    const w = Math.abs(selection.endX - selection.startX);
    const h = Math.abs(selection.endY - selection.startY);

    if (w < 10 || h < 10) return;

    // 실제 이미지 좌표로 변환 (displaySize → imageSize)
    const img = imageRef.current;
    const scaleX = img.naturalWidth / window.innerWidth;
    const scaleY = img.naturalHeight / window.innerHeight;

    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = Math.round(w * scaleX);
    cropCanvas.height = Math.round(h * scaleY);
    const ctx = cropCanvas.getContext('2d')!;

    ctx.drawImage(
      img,
      Math.round(x * scaleX), Math.round(y * scaleY),
      cropCanvas.width, cropCanvas.height,
      0, 0,
      cropCanvas.width, cropCanvas.height,
    );

    cropCanvas.toBlob((blob) => {
      if (blob) onCapture(blob);
    }, 'image/png');
  }, [selection, onCapture]);

  // ── 선택 초기화 ──
  const handleReset = () => {
    setSelection(null);
  };

  // ── 크롭 없이 전체 이미지 사용 ──
  const handleUseWhole = () => {
    onCapture(fullScreenBlob);
  };

  // ── ESC 키로 닫기 ──
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const hasValidSelection = selection &&
    Math.abs(selection.endX - selection.startX) > 10 &&
    Math.abs(selection.endY - selection.startY) > 10;

  return (
    <div
      data-screenshot-exclude
      className="fixed inset-0"
      style={{ zIndex: 100000, cursor: 'crosshair' }}
    >
      {/* 캔버스 */}
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      />

      {/* 도구 모음 */}
      <div
        className="fixed bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-card border border-border rounded-2xl shadow-xl p-2"
        style={{ zIndex: 100001 }}
      >
        {hasValidSelection && (
          <button
            onClick={handleConfirm}
            className="p-2.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            title="선택 영역 사용"
          >
            <Check className="w-5 h-5" />
          </button>
        )}
        {/* 크롭 없이 전체 이미지 사용 */}
        <button
          onClick={handleUseWhole}
          className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl hover:bg-muted text-muted-foreground transition-colors"
          title="크롭 없이 전체 화면 사용"
        >
          <Maximize className="w-5 h-5" />
          <span className="text-sm font-medium">전체 사용</span>
        </button>
        <button
          onClick={handleReset}
          className="p-2.5 rounded-xl hover:bg-muted text-muted-foreground transition-colors"
          disabled={!selection}
          title="선택 초기화"
        >
          <RotateCcw className="w-5 h-5" />
        </button>
        <button
          onClick={onCancel}
          className="p-2.5 rounded-xl hover:bg-destructive/10 text-destructive transition-colors"
          title="취소"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* 안내 텍스트 */}
      {!selection && (
        <div
          className="fixed top-8 left-1/2 -translate-x-1/2 rounded-xl px-5 py-2.5 text-sm font-medium text-white shadow-lg"
          style={{ zIndex: 100001, background: 'rgba(0, 0, 0, 0.75)', backdropFilter: 'blur(8px)' }}
        >
          드래그해 영역을 선택하거나, 전체 사용을 눌러 크롭 없이 진행하세요
        </div>
      )}
    </div>
  );
};
